import { useEffect, useMemo, useState } from "react";

import {
  Button,
  RadioButton,
  TextArea,
  TextField,
  Table,
  TableMetadata,
  Select,
  Typography,
  useTable,
  AutocompleteMultipleSelect,
  Label,
  InfoPanel,
  TableStateWithFilters,
  Checkbox,
  ColumnSortEnum,
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col, Tab, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { CommitteeDateRangePicker } from "./components";
import { DEFAULT_REGIONAL_COMMITTEE } from "./constants";
import {
  saveRegionalCommittee,
  searchInstitutionRegionalCommittee,
} from "../../api/Committee/RegionalCommitteeApi";
import {
  searchMunicipalities,
  searchTowns,
} from "../../api/Geography/GeographyApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  ListTable,
  SearchCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { useAuthCounties, useInput, useEnumCodeBooks } from "../../hooks";
import { ValidationFailure } from "../../models";
import {
  RegionalCommittee,
  CommitteeEmployee,
  RegionalCommitteeInstitution,
  RegionalCommitteeInstitutionSearch,
  RegionalCommitteeInstitutionSort,
  CommitteeType,
} from "../../models/Committee";
import {
  GrandParentCodeBookPagedFilter,
  HierarchicalCodeBook,
  HierarchicalCodeBookPagedFilter,
  SaveResult,
  Selectable,
} from "../../models/Shared";
import { useGetInstitutionListForUserQuery } from "../../queries/codeBookQuery";
import { useGetRegionalCommitteeByIdQuery } from "../../queries/committeeQuery";
import { regionalCommitteeEditUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { FormatUtils, TableUtils, ObjectUtils } from "../../utils";
import { useDeleteCommittee } from "./hooks/useDeleteCommittee";
import {
  generatingEditedEmployee,
  getCommitteeEmployeeDisplayName,
  isEmployeeActive,
  updateEmployeesWithPresident,
  replaceEmployeeInTableData,
} from "./utils";
import {
  setupCommitteeEmployees,
  updateCommitteeEmployee,
} from "./utils/committee.util";
import { CommitteeModal } from "./components/CommitteeModal";

const DEFAULT_TAB = "members";
const storageKey = "RegionalCommitteeInstitutionSearchTableState";

export function RegionalCommitteeEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { handleChange } = useInput();
  const dispatch = useAppDispatch();
  const authCounties = useAuthCounties();
  const now = new Date();

  const [item, setItem] = useState<RegionalCommittee>(() => {
    const defaultItem = { ...DEFAULT_REGIONAL_COMMITTEE };
    defaultItem.countyId = authCounties?.length > 0 ? authCounties[0].id : 1;
    defaultItem.dateFrom = new Date(
      now.getFullYear() + 1,
      now.getMonth(),
      now.getDate()
    );
    defaultItem.dateTo = new Date(
      now.getFullYear() + 3,
      now.getMonth(),
      now.getDate()
    );
    return defaultItem;
  });

  const [employeeTableData, setEmployeeTableData] = useState<
    CommitteeEmployee[]
  >([]);
  const employeeTableMetadata: TableMetadata<CommitteeEmployee> = {
    rowKey: "rowKeyId",
    columns: [
      {
        headerClassName: "col-4",
        columnKey: "expertCommissionRole",
        header: t("common.expertCommission.role"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeeRole?.name}</div>;
        },
      },
      {
        headerClassName: "col-5",
        columnKey: "employee",
        header: t("common.expertCommission.employee"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{getCommitteeEmployeeDisplayName(item)}</div>;
        },
      },
      {
        headerClassName: "col-4",
        columnKey: "activeTo",
        header: t("common.expertCommission.activeTo"),
        accessor: (item) => {
          return <div>{FormatUtils.formatDate(item.deactivationDate)}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "expertCommissionPosition",
        header: t("common.expertCommission.position"),
        accessor: (item: CommitteeEmployee) => {
          return <div>{item.committeePosition?.name}</div>;
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "president",
        header: t("common.expertCommission.president"),
        accessor: (emp: CommitteeEmployee) => {
          const isActive = isEmployeeActive(emp);

          return (
            <div className="pt-2 ml-3">
              <RadioButton
                ariaLabel="item.president"
                key={emp.rowKeyId!}
                name={"item.president"}
                checked={emp.isPresident}
                onChange={() => handlePresidentChange(emp.rowKeyId!)}
                value={emp.rowKeyId!}
                disabled={!emp.employee || !isEditing || !isActive}
              />
            </div>
          );
        },
      },
      {
        headerClassName: "col-1",
        columnKey: "edit",
        header: t("common.edit"),
        accessor: (employee) => {
          return (
            <div className={"text-right"}>
              <Button
                size="sm"
                outline={true}
                onClick={() => handleDetailsClicked(employee)}
                disabled={!isEditing}
              >
                {t("common.edit")}
              </Button>
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  const [employeeTableProps] = useTable({
    metadata: employeeTableMetadata,
    data: employeeTableData,
    pagination: false,
    totalItems: 1,
    defaultSort: {},
  });

  const initialSearch: RegionalCommitteeInstitutionSearch = {
    countyId:
      item.countyId ?? (authCounties?.length > 0 ? authCounties[0].id : 1),
    dateFrom: new Date(now.getFullYear() + 1, now.getMonth(), now.getDate()),
    dateTo: new Date(now.getFullYear() + 3, now.getMonth(), now.getDate()),
    regionalCommitteeId: 0,
  };

  const defaultInstitutionTableData = TableUtils.tableDefaultData<
    RegionalCommitteeInstitution & Selectable
  >();

  const [institutionTableData, setInstitutionTableData] = useState<
    PagedResult<RegionalCommitteeInstitution & Selectable>
  >(defaultInstitutionTableData);

  const INITIAL_INSTITUTION_TABLE_STATE = useMemo<
    TableStateWithFilters<RegionalCommitteeInstitutionSearch>
  >(
    () =>
      TableUtils.CreateInitialTableState<RegionalCommitteeInstitutionSearch>(
        storageKey,
        TableUtils.tableDefaultState(
          RegionalCommitteeInstitutionSort.Code,
          ColumnSortEnum.ASC
        )
      ),
    []
  );

  const institutionTableMetadata: TableMetadata<
    RegionalCommitteeInstitution & Selectable
  > = {
    rowKey: "id",
    columns: [
      {
        columnKey: "select",
        header: "",
        accessor: (item: RegionalCommitteeInstitution & Selectable) => {
          return (
            <div>
              <Checkbox
                label={""}
                name={nameof<typeof item>("id")}
                value={item.id}
                checked={item.isSelected}
                onChange={handleInstitutionCheckedChange}
              />
            </div>
          );
        },
      },
      {
        columnKey: "code",
        header: t("common.institutionList.programCode"),
        accessor: (item: RegionalCommitteeInstitution) => {
          return <div>{item.code}</div>;
        },
        sortBy: RegionalCommitteeInstitutionSort.Code,
      },
      {
        columnKey: "town",
        header: t("common.institutionList.townName"),
        accessor: (item: RegionalCommitteeInstitution) => {
          return <div>{item.town}</div>;
        },
        sortBy: RegionalCommitteeInstitutionSort.Town,
      },
      {
        columnKey: "institutionName",
        header: t("common.institutionList.institutionName"),
        accessor: (item: RegionalCommitteeInstitution) => {
          return <div>{item.name}</div>;
        },
        sortBy: RegionalCommitteeInstitutionSort.InstitutionName,
      },
      {
        columnKey: "regionalCommitteeName",
        header: t("common.expertCommission.regionalCommitteeName"),
        accessor: (rcInstitution: RegionalCommitteeInstitution) => {
          return (
            <div>
              {item.institutionList?.some((v) => v === rcInstitution.id)
                ? item.name
                : ""}
            </div>
          );
        },
        sortBy: undefined,
      },
    ],
    rowToolTip: () => undefined,
  };

  const [
    institutionTableProps,
    institutionTableState,
    setInstitutionTableFilters,
  ] = useTable<RegionalCommitteeInstitutionSearch>({
    metadata: institutionTableMetadata,
    data: institutionTableData!.results,
    pagination: true,
    paginationPage: INITIAL_INSTITUTION_TABLE_STATE.page,
    paginationPageSize: INITIAL_INSTITUTION_TABLE_STATE.pageSize as any,
    totalItems: institutionTableData!.rowCount,
    defaultSort: {
      order: INITIAL_INSTITUTION_TABLE_STATE.order,
      sortBy: INITIAL_INSTITUTION_TABLE_STATE.sortBy,
    },
  });

  const [search, setSearch] =
    useState<RegionalCommitteeInstitutionSearch>(initialSearch);

  const [tableSearch, setTableSearch] =
    useState<RegionalCommitteeInstitutionSearch>(initialSearch);

  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [editing, setEditing] = useState<CommitteeEmployee | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tabKey, setTabKey] = useState(DEFAULT_TAB);
  const { data: institutions } = useGetInstitutionListForUserQuery();

  const [filteredMunicipalities, setFilteredMunicipalities] = useState<
    HierarchicalCodeBook[]
  >([]);
  const [filteredTowns, setFilteredTowns] = useState<HierarchicalCodeBook[]>(
    []
  );
  const [filterChanged, setFilterChanged] = useState(false);
  const [clearFilter, setClearFilter] = useState(true);
  const [selectAll, setSelectAll] = useState(false);

  const { data: committeeData, refetch: refetchCommitteeData } =
    useGetRegionalCommitteeByIdQuery(!isNaN(+id) ? +id : 0, {
      skip: isNaN(+id),
    });

  const { handleDeleteCommittee, isDeleteEnabled } = useDeleteCommittee({
    isEnabled: !item.hasDecisions,
    committeeType: "regional",
    committeeId: +id,
  });

  useEffect(() => {
    if (!isNaN(+id)) {
      if (committeeData) {
        setupCommitteeEmployees(committeeData, setItem, setEmployeeTableData);
      }
      if (+id === 0) {
        setIsEditing(true);
      }
    }
    initialSearch.regionalCommitteeId = +id;
    setClearFilter(true);
    const institutionSearch: RegionalCommitteeInstitutionSearch = {
      ...search,
      regionalCommitteeId: +id,
      dateFrom: item.dateFrom ? new Date(item.dateFrom) : new Date(),
      dateTo: item.dateTo ? new Date(item.dateTo) : new Date(),
    };
    setSearch(institutionSearch);
    setInstitutionTableFilters(institutionSearch);
    if (+id === 0) dispatch(setIsEdited());
    else dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, committeeData]);

  useEffect(() => {
    initialSearch.countyId = item.countyId;
    setClearFilter(true);
    const newSearch: RegionalCommitteeInstitutionSearch = {
      ...search,
      countyId: item.countyId,
    };
    setSearch(newSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.countyId]);

  useEffect(() => {
    handleSearchClicked(null);
  }, [search.countyId]);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    (async () => {
      const request =
        TableUtils.GetSearchRequest<RegionalCommitteeInstitutionSearch>(
          tableSearch,
          institutionTableState
        );
      const itemList = await searchInstitutionRegionalCommittee(request);
      if (itemList !== null && itemList.results.length > 0) {
        const pagedResult: PagedResult<
          RegionalCommitteeInstitution & Selectable
        > = {
          ...itemList,
          results: ObjectUtils.makeSelectableArray(itemList.results),
        };
        setInstitutionTableData(pagedResult);
      } else {
        setInstitutionTableData(defaultInstitutionTableData);
      }
      // Any time we clear the Update entity, we don't have anything selected.
      setSelectAll(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionTableState]);

  const handleDetailsClicked = (
    expertCommissionEmployee: CommitteeEmployee
  ) => {
    setEditing(expertCommissionEmployee);
  };

  const handleDateChange = (date: Date, key: "dateFrom" | "dateTo") => {
    const newSearch: RegionalCommitteeInstitutionSearch = {
      ...search,
      [key]: date,
    };
    setSearch(newSearch);
    const newItem: RegionalCommittee = {
      ...item,
      [key]: date,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };

  const handlePresidentChange = (rowKeyId: CommitteeEmployee["rowKeyId"]) => {
    const updatedEmployees = updateEmployeesWithPresident(
      employeeTableData,
      rowKeyId
    );

    setEmployeeTableData(updatedEmployees);
    setItem({
      ...item,
      employees: updatedEmployees,
    });
    dispatch(setIsEdited());
  };

  const handleNoteChange = (e: any) => {
    const note = e.target.value;
    const newItem: RegionalCommittee = {
      ...item,
      note: note,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };

  const handleMunicipalityFilterChange = async (filter: string) => {
    const mSearch: HierarchicalCodeBookPagedFilter = {
      pageSize: 500,
      page: 1,
      ids: search.municipalityIds,
      parentId: [item.countyId],
      search: filter,
    };
    const result = await searchMunicipalities(mSearch);
    setFilteredMunicipalities(result.results);
  };

  const handleMunicipalityChange = (e: any) => {
    const municipalityList = e.target.value as HierarchicalCodeBook[];
    const townList = filteredTowns.filter(
      (v) => municipalityList.find((c) => c.id === v.parentId) !== undefined
    );
    const newSearch: RegionalCommitteeInstitutionSearch = {
      ...search,
      municipalityIds: municipalityList?.map((v) => v.id),
      townIds: townList?.map((v) => v.id),
    };
    setSearch(newSearch);
  };

  const handleTownFilterChange = async (filter: string) => {
    const tSearch: GrandParentCodeBookPagedFilter = {
      pageSize: 500,
      page: 1,
      ids: search.townIds,
      parentId: search.municipalityIds,
      grandParentId: [item.countyId],
      search: filter,
    };
    const result = await searchTowns(tSearch);
    setFilteredTowns(result.results);
  };

  const handleTownChange = (e: any) => {
    const townList = e.target.value as HierarchicalCodeBook[];
    const newSearch: RegionalCommitteeInstitutionSearch = {
      ...search,
      townIds: townList?.map((v) => v.id),
    };
    setSearch(newSearch);
  };

  const handleInstitutionNameChange = (e: any) => {
    const name = e.target.value;
    const newSearch: RegionalCommitteeInstitutionSearch = {
      ...search,
      name: name,
    };
    setSearch(newSearch);
  };

  const handleSearchClicked = (e: any) => {
    setTableSearch(search);
    setFilterChanged(false);
    setInstitutionTableFilters({ ...tableSearch });
  };

  const handleClearSearchClicked = () => {
    setClearFilter(true);
    setSearch(initialSearch);
    setTableSearch(initialSearch);
    setInstitutionTableFilters({ ...tableSearch });
  };

  const handleInstitutionCheckedChange = (e: any) => {
    const institutionId = Number.parseInt(e.target.value);
    const isChecked = e.target.checked as boolean;
    const institutionArr = institutionTableData.results.slice();
    if (institutionId) {
      const institutionCodeBook = institutionArr.find(
        (val) => val.id === institutionId
      );
      if (isChecked) {
        institutionCodeBook!.isSelected = true;
      } else {
        setSelectAll(false);
        institutionCodeBook!.isSelected = false;
      }
      const newTableData: PagedResult<
        RegionalCommitteeInstitution & Selectable
      > = {
        ...institutionTableData,
        results: institutionArr,
      };
      setInstitutionTableData(newTableData);
    }
  };

  const handleSelectAllCheckedChange = (e: any) => {
    const isChecked = !selectAll;
    setSelectAll(isChecked);
    const newResults = institutionTableData.results.map((val) => {
      val.isSelected = isChecked;
      return val;
    });
    const newTableData: PagedResult<RegionalCommitteeInstitution & Selectable> =
      {
        ...institutionTableData,
        results: newResults,
      };
    setInstitutionTableData(newTableData);
  };

  const handleAssignmentClicked = (isAssigning: boolean) => {
    const institutionIdArr = institutionTableData.results
      .filter((v) => v.isSelected)
      .map((v) => v.id);
    let existingIdArr = item.institutionList?.slice() ?? [];
    if (isAssigning) {
      const newIdArr = institutionIdArr.filter(
        (i) => !existingIdArr.some((v) => v === i)
      );
      existingIdArr.push(...newIdArr);
    } else {
      existingIdArr = existingIdArr.filter(
        (e) => !institutionIdArr.some((v) => v === e)
      );
    }
    const newItem: RegionalCommittee = {
      ...item,
      institutionList: existingIdArr,
    };
    setItem(newItem);
  };

  const handleOnSaveCommitteeEmployee = (
    newCommitteeEmployee: CommitteeEmployee
  ) => {
    if (!editing) return;

    const employeeToEdit = generatingEditedEmployee(
      editing,
      newCommitteeEmployee
    );

    setEmployeeTableData((prev) =>
      replaceEmployeeInTableData(prev, employeeToEdit)
    );
    setItem((currItem) => updateCommitteeEmployee(currItem, employeeToEdit));
    setEditing(null);
  };

  const handleOnCloseModal = () => {
    setEditing(null);
  };

  const deleteButtonVisibility = isEditing
    ? isDeleteEnabled
      ? "show"
      : "disable"
    : "hide";

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={isEditing ? "hide" : "show"}
        pageTitle={t("common.expertCommission.regionPageNameEdit")}
        onClickEdit={() => {
          setIsEditing(true);
          dispatch(setIsEdited());
        }}
        errors={errors}
      />

      <FormCard>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.county")}
            </Typography>
          </Col>
          <Col md={4}>
            {isEditing && id === "0" && (
              <Select
                name={nameof<typeof item>("countyId")}
                value={item.countyId}
                onChange={handleChange(setItem)}
                selectData={codeBookToSelectData(authCounties)}
                plaintext={!isEditing || id !== "0"}
              />
            )}
            {(!isEditing || id !== "0") && (
              <Typography variant="base" color="light">
                {authCounties.find((v) => v.id === item.countyId)?.name}
              </Typography>
            )}
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.status")}
            </Typography>
          </Col>
          <Col md={4}>
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((v) => v.id === item.statusId)?.name}
            </Label>
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.name")}
            </Typography>
          </Col>
          <Col md={4}>
            <TextField
              name={nameof<typeof item>("name")}
              value={item.name}
              onChange={handleChange(setItem)}
              errorText={
                errors?.find((e) => e.propertyName === "Name")?.message
              }
              plaintext={!isEditing}
            />
          </Col>
          <Col md={2}>
            <Typography variant="base" color="light">
              {t("common.expertCommission.dateFromDateTo")}
            </Typography>
          </Col>
          <CommitteeDateRangePicker
            dateFrom={item.dateFrom ? new Date(item.dateFrom) : null}
            dateTo={item.dateTo ? new Date(item.dateTo) : null}
            onDateFromChange={(date) => handleDateChange(date, "dateFrom")}
            onDateToChange={(date) => handleDateChange(date, "dateTo")}
            errors={errors}
            disabled={!isEditing}
            plaintext={!isEditing}
            colMd={2}
          />
        </Row>
        <Row>
          <Col md={12}>
            <TextArea
              ariaLabel="item.note"
              label={t("common.expertCommission.note")}
              name={"item.note"}
              value={item.note ?? ""}
              onChange={handleNoteChange}
              errorText={
                errors?.find((e) => e.propertyName === "Note")?.message
              }
              maxLength={5000}
              disabled={!isEditing}
            />
          </Col>
        </Row>
      </FormCard>
      <Tabs
        id="controlled-tab-example"
        activeKey={tabKey ?? "basicInfo"}
        onSelect={(k) => setTabKey(k ?? DEFAULT_TAB)}
      >
        <Tab
          title={t("common.expertCommission.tabs.memberList")}
          eventKey={DEFAULT_TAB}
          className={
            errors?.some((e) => e.propertyName?.startsWith("Employees"))
              ? "text-danger"
              : undefined
          }
        >
          <FormCard>
            <Table {...employeeTableProps} />
          </FormCard>
        </Tab>
        <Tab
          title={t("common.expertCommission.tabs.institutionList")}
          eventKey="institutions"
          disabled={+id === 0}
          className={
            errors?.some((e) => e.propertyName?.startsWith("InstitutionList"))
              ? "text-danger"
              : undefined
          }
        >
          {(item.institutionList?.length ?? 0) > 0 && (
            <ul>
              {item.institutionList?.map((v) => (
                <li key={v}>
                  <Typography component="span">
                    {institutions?.find((i) => i.id === v)?.name}
                  </Typography>
                </li>
              ))}
            </ul>
          )}
          {(item.institutionList?.length ?? 0) === 0 && (
            <InfoPanel
              color="danger"
              message={t("common.expertCommission.noInstitutionMessage")}
              fullWidth={true}
            />
          )}
        </Tab>
        <Tab
          title={t("common.expertCommission.tabs.institutionAdd")}
          eventKey="addInstitutions"
          disabled={+id === 0 || !isEditing}
        >
          <SearchCard
            onClickSearch={handleSearchClicked}
            onClickClearSearch={handleClearSearchClicked}
            isFilterChanged={filterChanged}
            hideDivider={true}
          >
            <Row>
              <Col md={6}>
                <AutocompleteMultipleSelect<HierarchicalCodeBook>
                  label={t("common.schoolCandidateModel.municipalityList")}
                  name={nameof<typeof search>("municipalityIds")}
                  value={filteredMunicipalities.filter((v) =>
                    search.municipalityIds?.some((val) => val === v.id)
                  )}
                  valueProp="id"
                  textProp="name"
                  onChange={handleMunicipalityChange}
                  selectOptions={filteredMunicipalities}
                  onFilterChange={handleMunicipalityFilterChange}
                  maximumSelectedItems={10}
                />
              </Col>
              <Col md={6}>
                <TextField
                  label={t("common.schoolCandidateModel.institutionList")}
                  name={nameof<typeof search>("name")}
                  value={search.name}
                  onChange={handleInstitutionNameChange}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <AutocompleteMultipleSelect<HierarchicalCodeBook>
                  label={t("common.schoolCandidateModel.townList")}
                  name={nameof<typeof search>("townIds")}
                  value={filteredTowns.filter((v) =>
                    search.townIds?.some((val) => val === v.id)
                  )}
                  valueProp="id"
                  textProp="name"
                  onChange={handleTownChange}
                  selectOptions={filteredTowns}
                  onFilterChange={handleTownFilterChange}
                  maximumSelectedItems={10}
                />
              </Col>
            </Row>
          </SearchCard>
          <FormCard
            cardTitle={t("common.schoolCandidateModel.schoolCandidateAssign")}
          >
            <Row className="m-1 pb-3 pt-3 bg-light border">
              <Col md={2} className="pt-4">
                <Checkbox
                  label={t("common.selectAll")}
                  name={"selectAll"}
                  checked={selectAll}
                  onChange={handleSelectAllCheckedChange}
                />
              </Col>
              <Col md={2} className="pt-4">
                <Button
                  onClick={() => handleAssignmentClicked(false)}
                  outline={true}
                  size="sm"
                >
                  {t("common.schoolCandidateModel.clearAssignments")}
                </Button>
              </Col>
              <Col md={2} className="pt-4">
                <Button onClick={() => handleAssignmentClicked(true)} size="sm">
                  {t("common.expertCommission.addAssignments")}
                </Button>
              </Col>
            </Row>
          </FormCard>
          <FormCard>
            <ListTable tableProps={institutionTableProps} />
          </FormCard>
        </Tab>
      </Tabs>

      <ToolbarEdit
        isDataEdited={isEditing}
        showCancel={"show"}
        showSave={isEditing ? "show" : "hide"}
        saveText={t("common.saveAndApprove")}
        handleSave={async () => saveRegionalCommittee(item)}
        redirectUrlSaveOK={regionalCommitteeEditUrl}
        setSaveErrors={setErrors}
        onSaveSuccess={(data: SaveResult<RegionalCommittee>) => {
          if (!data.result) return;

          setItem(data.result);
          refetchCommitteeData();
        }}
        showDelete={deleteButtonVisibility}
        onClickDelete={handleDeleteCommittee}
      />
      {/* CommitteeEmployee Modal */}
      <CommitteeModal
        committeeType={CommitteeType.Regional}
        editingEmployee={editing ?? null}
        onSave={handleOnSaveCommitteeEmployee}
        onClose={handleOnCloseModal}
      />
    </ContentArea>
  );
}
